import React, { useContext } from 'react';
import classNames from 'classnames';
import { NdaStatus } from 'models/Nda';
import { useTranslation } from 'react-i18next';
import useNdaStatus, { UpdateLeadNdaProps } from 'hooks/useNdaStatus';
import {
  isNdaCounterSignerPending,
  isNdaNotSent,
  isNdaRejected,
  isNdaSent,
} from 'utils';
import { getFormattedDate } from 'dateUtils';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import { Button, Loader } from 'ncoded-component-library';
import VerifiedBadge from 'components/VerifiedBadge';
import LeadContext from '../../subrouters/Portfolio/subrouters/Buyer/providers/Lead/Lead.context';
import TransWithNda from 'components/TransWithNda';

import './BuyerNdaCard.styles.scss';

type BuyerNdaCardProps = {
  className?: string;
  leadId: string;
  signingStatus: NdaStatus;
  envelopeId: string;
  signedDate: Date;
  rejectionReason?: string;
  manuallySetNDAStatus: boolean;
  envelopeTemplateName: string;
  updateNdaProps: (leadNdaProps: UpdateLeadNdaProps) => void;
};

const BuyerNdaCard: React.FC<BuyerNdaCardProps> = (props) => {
  const {
    className,
    leadId,
    signingStatus,
    envelopeId,
    signedDate,
    rejectionReason,
    manuallySetNDAStatus,
    envelopeTemplateName,
    updateNdaProps,
  } = props;

  const { isWaitingForWebhook } = useContext(LeadContext);

  const classes = classNames('buyer-nda-card', className);

  const { t } = useTranslation();

  const { isLoading, getAndOpenNdaLink } = useNdaStatus(
    leadId,
    envelopeId,
    updateNdaProps,
  );

  if (isNdaNotSent(signingStatus)) return null;

  if (isNdaRejected(signingStatus))
    return (
      <div className={classes}>
        <p className="heading">
          <WarningExclamationIcon />
          <TransWithNda i18nKey="ndaRejected" />
        </p>

        <div className="container">
          <TransWithNda
            className="nda-label"
            i18nKey="userRejectedNDA"
            values={{
              name: t('you'),
              rejectionReason: rejectionReason || '/',
            }}
          />
        </div>
      </div>
    );

  if (isNdaCounterSignerPending(signingStatus))
    return (
      <div className={classes}>
        <p className="heading">
          <WarningExclamationIcon />
          {t('waitingNdaCounter')}
        </p>

        <div className="container">
          <p className="nda-label">{t('youSignedNDAWaitingCoutnersign')}</p>
        </div>
      </div>
    );

  if (isNdaSent(signingStatus)) {
    if (isWaitingForWebhook) {
      return (
        <div className={classes}>
          <p className="heading">{t('ndaProcessing')}</p>

          <div className="container">
            <p className="nda-label">{t('ndaIsBeingProcessing')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className={classes}>
        <p className="heading">
          <WarningExclamationIcon />
          {t('actionRequired')}
        </p>

        <div className="container">
          <p className="nda-label">{t('ndaIsReadyToSign')}</p>

          <Button
            variant="outline"
            disabled={isLoading}
            onClick={getAndOpenNdaLink}
          >
            {isLoading && <Loader />}
            {t('signNDA')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      <div className="container">
        <div className="document">
          <VerifiedBadge />
          <div>
            <p>{envelopeTemplateName || t('nonDisclosureAgreement')}</p>
            <p className="document-date">{getFormattedDate(signedDate)}</p>
          </div>
        </div>

        {!manuallySetNDAStatus && (
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={getAndOpenNdaLink}
          >
            {isLoading && <Loader />}
            {t('viewSignedNDA')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BuyerNdaCard;
